/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { switchMap, map, catchError, takeUntil, exhaustMap, tap, mapTo } from 'rxjs/operators';
import { cashboxesClient } from '../../api/cashBoxes/cashboxesClient';
import { from, of } from 'rxjs';
import {
    CashBoxDetailsAction,
    CashBoxDetailsActionType,
    fetchCashBoxDetailsSuccessAction,
    cashBoxDetailsErrorAction,
    fetchCashBoxTurnoversSuccessAction,
    fetchCashBoxTurnoversErrorAction,
    downloadTurnoversSuccessAction,
    downloadTurnoversErrorAction,
    changeCashBoxIntegrationStatusActionSuccess,
    blockCashboxSuccessAction,
    unblockCashboxSuccessAction,
    removeBlockUnblockScheduleSuccessAction,
    changeCashBoxStatusSuccessAction,
} from '../../actions/CashBoxRegistry/cashBoxDetailsActions';
import { downloadDataAsFile, parseError } from '../../../utils/utils';
import { i18n } from '../../translation/i18n';
import { RootState } from '../../store.typings';
import { AxiosError } from 'axios';

type CashBoxDetailsEpic = Epic<CashBoxDetailsAction, CashBoxDetailsAction, RootState>;

export const fetchCashBoxDetailsEpic: Epic<CashBoxDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CashBoxDetailsActionType.OPEN_REGISTRY_CASHBOX_CARD_PAGE,
            CashBoxDetailsActionType.CHANGE_CASHBOX_STATUS_SUCCESS,
            CashBoxDetailsActionType.BLOCK_CASHBOX_SUCCESS,
            CashBoxDetailsActionType.UNBLOCK_CASHBOX_SUCCESS,
            CashBoxDetailsActionType.REMOVE_BLOCK_UNBLOCK_SCHEDULE_SUCCESS,
            CashBoxDetailsActionType.CHANGE_CASHBOX_INTEGRATION_STATUS_SUCCESS,
        ),
        switchMap(() => {
            const { CashboxTaxNumber } = state$.value.routerState.route.params;

            return from(cashboxesClient.getCashBox(CashboxTaxNumber)).pipe(
                map((resp) => {
                    return fetchCashBoxDetailsSuccessAction(resp);
                }),

                catchError((err: AxiosError) =>
                    of(
                        cashBoxDetailsErrorAction(
                            parseError(
                                err,
                                i18n.t('RegistryScreens.errors.cashboxNotFound', { searchText: CashboxTaxNumber }),
                            ),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(CashBoxDetailsActionType.EXIT_REGISTRY_CASHBOX_CARD_PAGE)),
            );
        }),
    );
};

export const changeCashBoxStatusEpic: Epic<CashBoxDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashBoxDetailsActionType.CHANGE_CASHBOX_STATUS),
        switchMap((action) => {
            const { CashboxTaxNumber } = state$.value.routerState.route.params;
            return from(cashboxesClient.changeCashBoxStatus(CashboxTaxNumber, action.payload)).pipe(
                map(() => {
                    return changeCashBoxStatusSuccessAction();
                }),

                catchError((err: AxiosError) =>
                    of(
                        cashBoxDetailsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.changeCashBoxStatusFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(CashBoxDetailsActionType.EXIT_REGISTRY_CASHBOX_CARD_PAGE)),
            );
        }),
    );
};

export const blockCashBoxEpic: Epic<CashBoxDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashBoxDetailsActionType.BLOCK_CASHBOX),
        switchMap((action) => {
            const { CashboxTaxNumber } = state$.value.routerState.route.params;

            return from(cashboxesClient.blockCashBox(CashboxTaxNumber, action.payload)).pipe(
                map(() => {
                    return blockCashboxSuccessAction();
                }),

                catchError((err: AxiosError) =>
                    of(cashBoxDetailsErrorAction(parseError(err, i18n.t('RegistryScreens.errors.blockCashBoxFailed')))),
                ),

                takeUntil(action$.ofType(CashBoxDetailsActionType.EXIT_REGISTRY_CASHBOX_CARD_PAGE)),
            );
        }),
    );
};

export const unblockCashBoxEpic: Epic<CashBoxDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashBoxDetailsActionType.UNBLOCK_CASHBOX),
        switchMap((action) => {
            const { CashboxTaxNumber } = state$.value.routerState.route.params;

            return from(cashboxesClient.unblockCashBox(CashboxTaxNumber, action.payload)).pipe(
                map(() => {
                    return unblockCashboxSuccessAction();
                }),

                catchError((err: AxiosError) =>
                    of(
                        cashBoxDetailsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.unblockCashBoxFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(CashBoxDetailsActionType.EXIT_REGISTRY_CASHBOX_CARD_PAGE)),
            );
        }),
    );
};

export const removeBlockUnblockScheduleCashBoxEpic: Epic<CashBoxDetailsAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashBoxDetailsActionType.REMOVE_BLOCK_UNBLOCK_SCHEDULE),
        switchMap(() => {
            const { CashboxTaxNumber } = state$.value.routerState.route.params;

            return from(cashboxesClient.removeBlockUnblockSchedule(CashboxTaxNumber)).pipe(
                map(() => {
                    return removeBlockUnblockScheduleSuccessAction();
                }),

                catchError((err: AxiosError) =>
                    of(
                        cashBoxDetailsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.removeBlockUnblockScheduleFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(CashBoxDetailsActionType.EXIT_REGISTRY_CASHBOX_CARD_PAGE)),
            );
        }),
    );
};

export const changeCashBoxIntegrationStatusEpic: Epic<CashBoxDetailsAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxDetailsActionType.CHANGE_CASHBOX_INTEGRATION_STATUS),
        switchMap((action) => {
            return from(cashboxesClient.changeCashBoxIntegrationStatus([action.payload])).pipe(
                map(() => {
                    return changeCashBoxIntegrationStatusActionSuccess();
                }),

                catchError((err: AxiosError) =>
                    of(
                        cashBoxDetailsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.updateCashBoxIntegrationFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(CashBoxDetailsActionType.EXIT_REGISTRY_CASHBOX_CARD_PAGE)),
            );
        }),
    );
};

export const fetchCashBoxPeriodTurnoversEpic: CashBoxDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(CashBoxDetailsActionType.FETCH_CASHBOX_TURNOVERS),

        switchMap((action) => {
            const { CashboxTaxNumber } = state$.value.routerState.route.params;

            return from(cashboxesClient.getCashBoxPeriodTurnovers(CashboxTaxNumber, action.payload)).pipe(
                map((resp) => {
                    return fetchCashBoxTurnoversSuccessAction({ turnovers: resp, params: action.payload });
                }),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashBoxTurnoversErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.turnoversRequestFailed')),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const cashBoxTurnoversDownloadEpic: Epic<CashBoxDetailsAction, CashBoxDetailsAction, RootState> = (
    action$,
    state$,
) => {
    return action$.pipe(
        ofType(CashBoxDetailsActionType.DOWNLOAD_TURNOVERS),
        exhaustMap((action) => {
            const params = action.payload;
            const taxNumber = state$.value.routerState.route.params.CashboxTaxNumber;
            const fileName = `cashbox_${taxNumber}_TurnoversByPeriod.xlsx`;

            return from(cashboxesClient.downloadCashBoxTurnoversAsExcel(taxNumber, params)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadTurnoversSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadTurnoversErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};
